/**
 * The site-wide loyalty toast layer.
 *
 * One export, called once from resources/scripts/loyalty.jsx. Everything a page needs in order to
 * congratulate a fan on reaching a tier or unlocking a badge, on any page of the site, with no
 * markup to hang off and nothing decided in PHP.
 *
 *   gate.js      who is eligible, and what asking costs
 *   enrolled.js  which fan the remembered enrolment verdict belongs to
 *   identity.js  whose baseline the snapshot holds, and dropping it when the fan changes
 *   watermark.js how far through the feed this tab has already looked
 *   queue.jsx    the root, the array, and the library's viewport
 *   trigger.js   when to look, and what to do when looking fails
 *
 * The whole layer is inert for anyone who is not a signed-in, enrolled fan: no DOM node, no client,
 * no request. That is what makes it affordable to ship on every page.
 */

import { reconcileLoyaltyToasts } from '@drewling/urc-loyalty-ui';
import { getClient } from '../client';
import { VERDICT, whenToastEligible } from './gate';
import { mountToastLayer } from './queue';
import { reconcileToasts, holdToasts, startToastTrigger } from './trigger';
import { forgetToastBaseline } from './identity';

/**
 * Where the layer says what it is doing: <html data-loyalty-toast="...">.
 *
 * The `data-join-strip-state` precedent, moved onto an element that always exists -- so a visitor
 * for whom the layer never arms still gets the diagnostic, at no DOM cost. Every terminal verdict
 * names its own cause, which is the difference between "the toasts are broken" and "this fan has
 * not joined".
 *
 * @param {string} state One of gate.js's VERDICT values, or a trigger state.
 * @return {void}
 */
function publish( state ) {
	document.documentElement.dataset.loyaltyToast = state;
}

/** Once per page. A second call is a no-op, not a second layer. */
let started = false;

/**
 * @return {void}
 */
export function initLoyaltyToasts() {
	if ( started ) {
		return;
	}

	started = true;

	publish( 'anon' );

	whenToastEligible( verdict => {
		publish( verdict );

		if ( verdict !== VERDICT.ARMED ) {
			return;
		}

		// Mount BEFORE the trigger runs. The viewport's live region has to exist, and be empty,
		// before anything is pushed into it -- see queue.jsx.
		mountToastLayer();
		startToastTrigger( publish );

		if ( import.meta.env.DEV ) {
			installDevHook();
		}
	} );
}

/**
 * `window.urcLoyaltyToasts` -- a way to see a toast without waiting for a real award.
 *
 * Behind `import.meta.env.DEV`, which Vite replaces with a literal `false` at build time, so the
 * whole function is dropped from the production bundle. Verify with:
 * `grep urcLoyaltyToasts public/loyalty-*.js` after a build -- it must find nothing.
 *
 * Everything it needs is imported STATICALLY at the top of this file, even though a dynamic import
 * would look tidier for dev-only code. A dynamic import is a separate Rollup chunk, and a chunk
 * carrying CSS emits its own stylesheet that App\Loyalty\LoyaltyAssets never enqueues -- the exact
 * silent-unstyled failure the theme's vite.config.ts already works around twice. These three
 * symbols are in the bundle regardless (trigger.js and identity.js import them), so the static form
 * costs nothing.
 *
 * @return {void}
 */
function installDevHook() {
	window.urcLoyaltyToasts = {
		/** Freeze every subsequent toast on screen, for a capture or an inspection. */
		hold: holdToasts,

		/** Run the ordinary catch-up diff. Shows nothing unless something genuinely changed. */
		reconcile: reconcileToasts,

		/**
		 * Treat the fan's CURRENT state as if it had just been earned.
		 *
		 * Not the obvious thing, and the obvious thing does not work: clearing
		 * `urc.loyalty.toastSnapshot` and reloading produces no toast at all, because a missing
		 * snapshot reads as a first run, which returns nothing and silently re-baselines.
		 *
		 * So this plants a deliberately-behind baseline instead -- bottom of the tier ladder, zero
		 * badges -- and only then reconciles against the real client, at which point every earned
		 * badge and the current tier read as new. Lifted from the library's own demo host.
		 *
		 * @return {Promise<boolean>} Whether the real reconcile ran.
		 */
		replay: async () => {
			const client = getClient();
			const [ progress, ladder ] = await Promise.all( [
				client.getTierProgress(),
				client.getTiers(),
			] );

			// The mark goes wherever the snapshot goes -- one call, so the pairing cannot be got
			// wrong here either. Harmless for this call, which reconciles directly, but a mark left
			// behind would let the NEXT page load skip its reconcile while the stored snapshot is
			// the synthetic baseline planted below.
			forgetToastBaseline();

			await reconcileLoyaltyToasts(
				{
					getTierProgress: () =>
						Promise.resolve( {
							...progress,
							currentTierName: ladder[ 0 ]?.name ?? '',
						} ),
					getTiers: () => Promise.resolve( ladder ),
					getBadgesDetail: () =>
						Promise.resolve( {
							summary: { earned: 0, inProgress: 0, locked: 0, total: 0 },
							badges: [],
						} ),
				},
				{ autoDismissMs: null }
			);

			return reconcileToasts();
		},
	};
}
